import React, {useState, useRef} from "react";
import {Box, Button, Divider, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper} from "@mui/material";
import FaModalDialog from "../FactureeComponents/FaModalDialog";
import ShowCode from "../helpers/ShowCode.js";

const FactureeCompModalDialogExmpl = () => {

  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<{ show: () => void }>(null);

  const openModal = () => {
    setIsModalOpen(true);
    modalRef.current?.show(); // Triggering the show method of the modal
  };

  const exampleCode = `
<>
  {/* Button to trigger modal */}
  <Button variant={"contained"} onClick={() => console.log('Modal would open')}>Open Modal</Button>

  {/* Modal dialog component */}
  <FaModalDialog
    title="Example Modal"
    subtitleText="This is an optional subtitle text"
    maxWidth="sm"
    maxHeight={60}
    isOpen={true}
    onAccept={() => console.log('Accept clicked')}
    buttonTextAccept={"Custom Accept"}
    onReject={() => console.log('Reject clicked')}
    closeButtonVariant="text"
    footerText="Optional footer text before buttons"
  >
    <Typography>This is the content of the modal.</Typography>
  </FaModalDialog>
</>
`;

  return (
    <Box sx={{padding: '8rem 8vw 2rem 8vw'}}>
      <Typography variant={"h3"} sx={{marginBottom: '1rem'}}>FaModalDialog Component</Typography>

      <Typography variant={"h4"} sx={{marginBottom: '1.5rem', marginTop: '2.5rem'}}>Basic Modal Example</Typography>

      <Button variant={"contained"} onClick={openModal}>Open Modal</Button>

      <FaModalDialog
        ref={modalRef}
        title="Example Modal"
        subtitleText="This is an optional subtitle text"
        maxWidth="sm"
        maxHeight={60}
        isOpen={isModalOpen}
        onAccept={() => {
          // Handle accept action
          setIsModalOpen(false); // Close the modal
        }}
        buttonTextAccept={"Custom Accept"}
        onReject={() => {
          // Handle reject action
          setIsModalOpen(false); // Close the modal
        }}
        closeButtonVariant="text"
        footerText="Optional footer text before buttons"
      >
        {/* Content of the modal */}
        <Typography>This is the content of the modal.</Typography>
      </FaModalDialog>

      <Divider/>
      <ShowCode codeString={exampleCode} scope={{React, useState, useRef, Button, FaModalDialog, Typography}}/>

      <Typography variant={"h4"} sx={{marginBottom: '0.5rem', marginTop: '3rem'}}>API Reference</Typography>
      <Typography variant={"body2"} color="text.secondary" sx={{marginBottom: '2rem'}}>
        Complete list of all props supported by the FaModalDialog component.
      </Typography>

      <TableContainer component={Paper} sx={{marginBottom: '2rem', '& code': {fontSize: '0.875rem'}}}>
        <Table>
          <TableHead>
            <TableRow sx={{backgroundColor: 'primary.main'}}>
              <TableCell sx={{color: 'white', fontWeight: 'bold', width: '20%'}}>Prop</TableCell>
              <TableCell sx={{color: 'white', fontWeight: 'bold', width: '25%'}}>Type</TableCell>
              <TableCell sx={{color: 'white', fontWeight: 'bold', width: '15%'}}>Default</TableCell>
              <TableCell sx={{color: 'white', fontWeight: 'bold', width: '40%'}}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><code>title</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>string | null</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>The main title displayed in the modal header.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>subtitleText</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Additional subtitle text displayed below the title in the header section.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>maxWidth</code> *</TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>'xs' | 'sm' | 'md' | 'lg' | 'xl'</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Defines the maximum width of the modal dialog using Material-UI breakpoint values.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>maxHeight</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>number</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Sets the maximum height of the modal in viewport height units (vh).</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>onAccept</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>() =&gt; void</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Callback function triggered when the accept button is clicked; displays a contained "Accept" button in the footer.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>buttonTextAccept</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>"Accept"</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Custom text for the accept button.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>onOk</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>() =&gt; void</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Callback function triggered when the OK button is clicked; displays a contained "Ok" button with autofocus in the footer.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>buttonTextOk</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>"Ok"</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Custom text for the OK button.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>onReject</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>() =&gt; void</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Callback function triggered when the reject/cancel button is clicked; displays a reject button in the footer.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>buttonTextReject</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>"Reject"</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Custom text for the reject button.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>closeButtonVariant</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>'contained' | 'outlined' | 'text'</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>"contained"</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Variant style for the reject/close button.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>children</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>ReactNode</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>The content to be displayed inside the modal dialog body.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>showClose</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>boolean</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>When true, displays a close icon button in the top-right corner of the header.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>isOpen</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>boolean</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Controls the open state of the modal when used in controlled mode.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>noHeader</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>boolean</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>When true, hides the entire header section including title and subtitle.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>footer</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>ReactNode</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Custom footer content rendered above the action buttons.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>footerText</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>string</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Text displayed in the footer area before the action buttons.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>setIsOpen</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>(open: boolean) =&gt; void</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Callback function to update the modal's open state, typically used with controlled components.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>outsideClick</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>() =&gt; void</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Callback function triggered when the user clicks outside the modal (on the backdrop).</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>sx</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>SxProps&lt;Theme&gt;</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Material-UI sx prop for custom styling of the dialog component.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><code>ref</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>Ref</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
              <TableCell sx={{color: 'text.secondary'}}>Forwarded ref that exposes methods: <code>show()</code> to open the modal, <code>handleClose()</code> to close it, and <code>modalRef</code> for the underlying dialog reference.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant={"body2"} color="text.secondary" sx={{fontStyle: 'italic'}}>
        * Required prop
      </Typography>
      
    </Box>
  );
};

export default FactureeCompModalDialogExmpl;
